class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.renderSeq = 0;
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);

    // A cart mutation this page didn't originate (a third-party app calling
    // /cart/add.js on its own, for example) never reaches renderContents()
    // any other way — this keeps the drawer eventually consistent with the
    // server's actual cart regardless of who mutated it.
    window.__cartMutationWatchers = window.__cartMutationWatchers || new Set();
    this.resyncOnMutation = debounce(() => this.resync(), 400);
    window.__cartMutationWatchers.add(this.resyncOnMutation);
  }

  disconnectedCallback() {
    window.__cartMutationWatchers?.delete(this.resyncOnMutation);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    const token = this.beginRequest();

    fetch(this.dataset.url, { cache: 'no-store' })
      .then(response => response.text())
      .then(html => {
        if (!this.isCurrent(token)) return;

        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  // Every caller that is about to fetch a cart mutation (add, gift-wrap,
  // quantity change, ...) grabs a token here first. If a second, later
  // request starts before the first one's response arrives, the first
  // response is recognized as stale by renderContents() and dropped instead
  // of clobbering the newer content — otherwise whichever response happens
  // to arrive last wins, regardless of which request was actually last.
  beginRequest() {
    return ++this.renderSeq;
  }

  isCurrent(token) {
    return token === undefined || token === this.renderSeq;
  }

  renderContents(parsedState, token, options = {}) {
    if (!this.isCurrent(token)) return;

    this.productId = parsedState.id;

    // While the drawer is open (upsell adds, gift wrap, quick view),
    // patch the money-touched regions instead of swapping the whole
    // drawer — keeps open collapsibles, scroll and upsell state intact.
    const drawerOpen = this.drawer && !!this.drawer.querySelector('details[open]');

    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) return;

      if (
        section.id === 'mini-cart' &&
        drawerOpen &&
        window.theme && theme.patchMiniCart &&
        parsedState.sections[section.id] &&
        theme.patchMiniCart(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector))
      ) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // A background resync must not pop the drawer open — only a render
    // caused by something the shopper actually did should do that.
    if (!options.silent) this.open();
  }

  // Re-fetches the sections this element renders and applies them if
  // nothing newer has started since. Called by the cart-mutation watchdog
  // (registered in connectedCallback) whenever any /cart/* request completes
  // — including ones this page's own code never initiated.
  resync() {
    // Deliberately does NOT call beginRequest(): this is a passive
    // reconciler, so it must never supersede a user-initiated update that
    // is still in flight (doing so would discard that update's render and
    // strand its loading state). Observe the current sequence instead and
    // apply only if nothing else has started or finished in the meantime.
    const token = this.renderSeq;
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = this.getSectionsToRender().map((section) => section.section).join(',');

    fetch(`${root}?sections=${sectionIds}`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((sections) => this.renderContents({ sections }, token, { silent: true }))
      .catch((e) => console.error(e));
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);
